import { areCybusDetailedServiceContainersEquals, areCybusServiceContainersEquals, Capability, type CybusServiceContainer } from '../../../../../../domain'

import type { PageSubscriptionsTypes, SingleSubscriptionsTypes, SubscriptionFilterArgs } from '../../../../../../application'

import type { BackendDefinition } from '../../../../../Connectware'
import { mapResourceNames } from '../../../../mappers'
import { type CommonContainer, ContainerStrategy } from './Base'
import { mapDetailedServiceContainer } from './Mappers'

type IdMetadata = Pick<CybusServiceContainer, 'name' | 'service'>

export class ServiceContainersStrategy extends ContainerStrategy<'serviceContainers', never, IdMetadata> {
    protected override readonly supportedFilters: (keyof SubscriptionFilterArgs)[] = ['service']

    protected override readonly areEquals = areCybusServiceContainersEquals

    protected readonly containerMetadataConfig: ContainerStrategy<'serviceContainers', never, IdMetadata>['containerMetadataConfig'] = {
        capability: Capability.SERVICE_CONTAINERS_READ,
        method: 'GET',
        path: '/api/containers/+/inspect',
    }

    protected readonly containersOrchestratorConfig: ContainerStrategy<'serviceContainers', never, IdMetadata>['containersOrchestratorConfig'] = {
        capability: Capability.SERVICE_CONTAINERS_READ,
        method: 'GET',
        path: '/api/containers/orchestrator',
    }

    protected readonly containersConfig: ContainerStrategy<'serviceContainers', never, IdMetadata>['containersConfig'] = {
        capability: Capability.SERVICE_CONTAINERS_READ,
        method: 'GET',
        path: '/api/containers',
    }

    protected readonly singleContainerConfig = null

    protected readonly mapSingleRetrieval = null

    override readonly requiredPermissions = [Capability.SERVICE_CONTAINERS_READ]

    // eslint-disable-next-line class-methods-use-this
    protected readonly mapIdAux = (id: string, { service: serviceFilter }: SubscriptionFilterArgs): IdMetadata | null => {
        const [service, name] = mapResourceNames(id)

        if (serviceFilter && serviceFilter !== service) {
            return null
        }

        return { name, service }
    }

    // eslint-disable-next-line class-methods-use-this
    protected readonly mapListContainer = (
        base: CommonContainer,
        idMetadata: IdMetadata,
        { Mounts }: BackendDefinition<'container-manager', 'ContainerResponse'>
    ): PageSubscriptionsTypes['serviceContainers'] => {
        return { ...base, ...idMetadata, volumesCount: Mounts.length }
    }
}

export class DetailedServiceContainerStrategy extends ContainerStrategy<never, 'serviceContainer', IdMetadata> {
    protected override readonly areEquals = areCybusDetailedServiceContainersEquals

    protected readonly singleContainerConfig: ContainerStrategy<never, 'serviceContainer', IdMetadata>['singleContainerConfig'] = {
        capability: Capability.SERVICE_CONTAINER_READ,
        method: 'GET',
        path: '/api/containers/+/inspect',
    }

    protected readonly containerMetadataConfig = null

    protected readonly containersOrchestratorConfig = null

    protected readonly containersConfig = null

    protected readonly mapIdAux = null

    protected readonly mapListContainer = null

    override readonly requiredPermissions = [Capability.SERVICE_CONTAINER_READ]

    // eslint-disable-next-line class-methods-use-this
    protected override mapSingleRetrieval = (
        id: string,
        data: BackendDefinition<'container-manager', 'ContainerResponse'>
    ): SingleSubscriptionsTypes['serviceContainer'] => mapDetailedServiceContainer(id, data)
}
